import KeycloakSingleton from '../features/Auth/utils/keycloakSingleton';
import { getKeycloakSettings } from '../features/Auth/utils/AuthUtils';
import socketFactory from '../utils/socketFactory';
import { sendTest } from '../store/socketSlice/socketSlice';
import { login,getUserPermissions, getLivekitUrl } from '../features/Auth/store/authThunks';
import { startAvatarsSync } from '../features/Map/store/Avatars/AvatarsSlice';
import { bindStore, veifyGuestToken } from '../features/Auth/utils/AuthUtils';
import { guestLoginDemo, guestLogin } from '../features/Auth/store/authThunks';

export class AppInitializer {
  static async retryOperation(operation, maxRetries = 3, delay = 2000) {
    let lastError;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        return await operation();
      } catch (error) {
        lastError = error;
        console.warn(`Operation failed (attempt ${attempt}/${maxRetries}):`, error);

        if (attempt < maxRetries) {
          const currentDelay = delay;
          console.log(`Retrying in ${currentDelay}ms...`);
          await new Promise(resolve => setTimeout(resolve, currentDelay));
          delay *= 1.5; // Exponential backoff
        }
        // If all attempts fail, refresh the page
        else {
          console.error('All attempts failed. Refreshing the page...');
          window.location.reload();
          return;
        }
      }
    }

    throw lastError;
  }

  static async getPreloadedState() {
    try {
      const isGuestRoute = window.location.pathname.startsWith('/guest');
     const isInscriptionRoute = window.location.pathname.startsWith('/conference/registration');  
     const isConferenceGuestonRoute = window.location.pathname.startsWith('/conference/guest');  
      if (isGuestRoute || isInscriptionRoute || isConferenceGuestonRoute ) {
        return {}; // No preloaded state for guest routes
      }

    
      console.log('Starting pre-store initialization...');

      // Step 1: Get Keycloak settings with retry
      const { keycloakSettings, tenant, livekitUrl } = await this.retryOperation(
        async () => {
          console.log('Fetching Keycloak settings...');
          return await getKeycloakSettings();
        },
        3, // max retries
        2000 // initial delay
      );

      const validGuestToken= veifyGuestToken();
          if(validGuestToken){
            return {
              isGuest : true,
              keycloakSettings,
              tenant,
              livekitUrl,
            }; 
          }

      let authState = {
        keycloakSettings,
        tenant,
        livekitUrl,
        isAuthenticated: false,
        token: null,
        user: null,
        error: null
      };

      // Step 2: Initialize Keycloak with retry
      if (keycloakSettings) {
        const authenticated = await this.retryOperation(
          async () => {
            console.log('Initializing Keycloak...');
          //  KeycloakSingleton.resetInstance(); // Reset before re-initializing
            const res =  await KeycloakSingleton.init(keycloakSettings);
            if(!res)
              throw new Error("Keycloak initialization failed");
            return res;
          },
          3, // max retries
          1500 // initial delay
        );

        if (authenticated) {
          const keycloakInstance = KeycloakSingleton.getInstance();
          authState.isAuthenticated = true;
          authState.token = keycloakInstance.token;

          // Extract user info from token if available
          if (keycloakInstance.tokenParsed) {
            authState.user = {
              id: keycloakInstance.tokenParsed.sub,
              email: keycloakInstance.tokenParsed.email,
              name: keycloakInstance.tokenParsed.name,
              preferred_username: keycloakInstance.tokenParsed.preferred_username,
              roles: keycloakInstance.tokenParsed.realm_access?.roles || [],
              // Add other token properties as needed
            };
          }

          console.log('Keycloak initialization successful');
        } else {
          console.log('User not authenticated via Keycloak');
        }
      }

      console.log('Pre-store initialization completed');
      return {
        auth: authState
      };

    } catch (error) {
      console.error('Failed to create preloaded state:', error);
      throw new Error(`Initialization failed: ${error.message}`);
    }
  }

  static async initializePostStoreServices(store, preloadedState) {


    console.log('Starting post-store service initialization...');

    try {
      // Step 1: Initialize socket factory with retry
      await this.retryOperation(
        async () => {
          console.log('Attaching store to socket factory...');
          socketFactory.attachStore(store);
          store.dispatch(sendTest({ type: "connect" }));
        },
        3,
        1000
      );

      // Step 2: Fetch additional user data with retry
      await this.retryOperation(
        async () => {
          if (!preloadedState.auth?.isAuthenticated) {
            if(preloadedState.isGuest && preloadedState.tenant){  
            const code  = localStorage.getItem('code');
            const isConferenceGuest = localStorage.getItem('isConferenceGuest');
              if (code) {
                if(isConferenceGuest){
                   await store.dispatch(
                  guestLogin({ body: { code}})
                );
                }
                else{
                  const username = localStorage.getItem('name');
                  const avatarImage = localStorage.getItem('avatarImage');
                  await store.dispatch(
                    guestLogin({ body: { code, username, url_photo: avatarImage} })
                  );
                }
                }
              else {
                const demoToken = localStorage.getItem("demoToken");
                const demoMapId = localStorage.getItem("demoMapId");

                if (demoToken || demoMapId) {
                  const username = localStorage.getItem("name");
                  const avatarImage = localStorage.getItem("avatarImage");
                  const email = localStorage.getItem("email");

                  const body = {
                    username,
                    url_photo: avatarImage,
                    user_email: email,
                    ...(demoToken
                    ? { demoToken }
                    : { map_id: demoMapId, tenant_id: preloadedState?.tenant?.id }),
                  };

                  await store.dispatch(guestLoginDemo({ body }));
                  await store.dispatch(getLivekitUrl({ params: [{ tenantId: preloadedState?.tenant?.id }] }));
                }
              }
                
            }

            console.log('User not authenticated, skipping additional data fetch');
            return;
          }
          console.log('Fetching additional user data...');
          await store.dispatch(login()).unwrap(); // unwrap to catch thunk errors
        },
        3,
        1500
      );

        // Step 3: Bind store + Fetch user permissions
    await this.retryOperation(
      async () => {
        console.log("Binding store to authUtils...");
        bindStore(store);

  const s = store.getState();
   if (s?.auth?.isAuthenticated && !s?.auth?.isGuest) {
     console.log("Preloaded KC user detected → Fetching user permissions...");
     await store.dispatch(getUserPermissions()).unwrap();
   } else {
     console.log("Guest or unauthenticated at preload → permissions will be fetched after login via listener.");
   }
    },
    3,
    1500
  );

      // Step 4: Start avatar sync with retry
      await this.retryOperation(
        async () => {
          console.log('Starting avatar synchronization...');
          store.dispatch(startAvatarsSync());
        },
        2, // fewer retries for avatar sync
        1000
      );

      console.log('Post-store services initialized successfully');

    } catch (error) {
      console.error('Failed to initialize post-store services:', error);
      // Don't throw here - we want the app to still load even if some services fail
      // The app can handle these services being unavailable
    }
  }
}
